import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { NAVIGATION } from '@/app/navigation'
import type { TimelineEvent } from '@/approvals/types'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { formatDate, formatMoney } from '@/lib/format'
import { CellValue, cardHref } from '@/lists/cellValue'
import { PlanActivityButtons } from '@/calendar/PlanActivityButtons'
import { postComment, searchMentionable } from '@/notifications/api'
import { feedItemOf as sharedFeedItemOf } from './recordHelpers'
import { TimelineMore, useTimeline } from './useTimeline'
import {
  completeActivity,
  createTask,
  fetchActivities,
  logCall,
  mayCompleteActivity,
  mayLogActivity,
  nextStepOf,
  type Activity,
} from './activityApi'
import { AttachmentsPanel } from './AttachmentsPanel'
import { CloseDealForm } from './CloseDealForm'
import { participantsTab } from './ContactTabs'
import { CustomFieldsPanel } from './CustomFieldsPanel'
import { ForecastFactors, formatPercent, noForecastText } from './ForecastChip'
import {
  deleteDeal,
  fetchDealCard,
  fetchDealQuotes,
  fetchFunnels,
  fetchStageHistory,
  moveDealToStage,
  type DealCard,
  type FunnelStage,
  type StageHistoryEntry,
} from './dealApi'
import { LinesPanel, StageHistoryPanel, formatDuration } from './DealTabs'
import { HistoryPanel } from './HistoryPanel'
import {
  Composer,
  ConfirmModal,
  Facts,
  Feed,
  NextStep,
  Panel,
  RecordHeader,
  RecordTabs,
  StageBar,
  StateView,
  type ComposerMode,
  type Fact,
  type FeedItem,
  type HeaderAction,
  type MenuAction,
  type StageStep,
} from './record'
import type { RecordViewProps } from './types'
import '@/styles/record.css'
import { useWriteEmail } from '@/mail/WriteEmail'
import { useCallAction } from '@/telephony/CallButton'
import { ConversationSummaryPanel } from '@/ai/ConversationSummary'
import { TaskProposalsPanel } from '@/ai/TaskProposals'
import { NextStepPanel } from '@/nextstep/NextStepPanel'

/**
 * Карточка сделки на дизайн-системе (Deal.dc.html) — образец для остальных карточек.
 *
 * Шапка отвечает на четыре вопроса макета: что это (название, номер), с кем (клиент-ссылка),
 * где мы (полоса стадий с днями) и что дальше («Следующий шаг» первым на обзоре). Вкладок
 * четыре вместо прежних девяти; прежнее содержимое не потеряно, а распределено:
 *
 * - **Обзор** — шаг, композер, лента; справа сведения (правка полей), позиции кратко,
 *   участники, доп. поля;
 * - **Позиции** — полная таблица с правкой и маржой;
 * - **Документы** — КП сделки, вложения, связанные;
 * - **История** — история стадий и журнал изменений.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - «Создать КП» и «Выставить счёт»: оба эндпоинта требуют **организацию**, которую не
 *   несут ни сделка, ни контрагент, а справочник организаций открыт только правам платежей
 *   и администрирования. Кнопка, которая угадывает юрлицо, положила бы документ на чужой
 *   бланк; кнопка без выбора — отказ сервера. Ждёт выбора организации с правом продаж.
 * - Основной контакт с телефоном в мета-строке: карточка сделки отдаёт `contactId` без
 *   имени и телефона. Участники с ролями — в панели справа.
 * - «Письмо» в композере: отправки письма из CRM нет (почтовая интеграция — заглушка #56).
 * - Имена авторов в ленте: событие несёт `actorId` без имени.
 * - Панели «Клиент» (долг, просрочка) и «Согласование скидки»: у сделки нет согласований
 *   (порог проверяется при отправке КП), а дебиторка клиента отдаётся суммой без разбивки
 *   по валютам — показать её одной цифрой значило бы выдумать курс.
 */
export function DealRecordView({ recordId, record, context, form, notices, edit }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const mail = useWriteEmail({ type: 'deal', id: recordId })
  // Through the PBX (#122): the server dials the deal's contact person, else its company.
  const dial = useCallAction({ type: 'deal', id: recordId }, null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [tab, setTab] = useState('overview')
  const [closing, setClosing] = useState<{ stageCode: string; kind: 'lost' | 'won' } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [composerMode, setComposerMode] = useState<string | undefined>(undefined)
  const [composerFocus, setComposerFocus] = useState(0)

  const deal = useQuery({
    queryKey: ['deal-card', recordId],
    queryFn: ({ signal }) => fetchDealCard(recordId, signal),
    retry: false,
  })
  const funnels = useQuery({ queryKey: ['funnels'], queryFn: ({ signal }) => fetchFunnels(signal) })
  const history = useQuery({
    queryKey: ['deal-stage-history', recordId],
    queryFn: ({ signal }) => fetchStageHistory(recordId, signal),
    retry: false,
  })
  const activities = useQuery({
    queryKey: ['deal-activities', recordId],
    queryFn: ({ signal }) => fetchActivities('deal', recordId, 100, signal),
    retry: false,
  })
  const timeline = useTimeline('deal', recordId)
  const mayReadQuotes = can(user, 'sales.quote', 'read')
  const quotes = useQuery({
    queryKey: ['deal-quotes', recordId],
    queryFn: ({ signal }) => fetchDealQuotes(recordId, signal),
    enabled: mayReadQuotes,
    retry: false,
  })

  const mayUpdate = can(user, 'sales.deal', 'update')
  const mayDelete = can(user, 'sales.deal', 'delete')

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['deal-card', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['record', 'sales.deal', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['deal-stage-history', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['timeline', 'deal', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['list', 'sales.deal'] })
    void queryClient.invalidateQueries({ queryKey: ['deal-board'] })
  }
  const refreshActivity = () => {
    void queryClient.invalidateQueries({ queryKey: ['deal-activities', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['timeline', 'deal', recordId] })
  }
  const fail = (error: unknown) => setFailure(error instanceof ApiError ? error.message : t('app.error'))

  // Same domain move as the board: stage history, outcome events and automation fire the
  // same way wherever the deal is moved from.
  const move = useMutation({
    mutationFn: ({ stageCode, reasonId, note }: { stageCode: string; reasonId: string | null; note: string | null }) =>
      moveDealToStage(recordId, stageCode, reasonId, note),
    onSuccess: () => {
      setClosing(null)
      setFailure(null)
      refresh()
    },
    onError: fail,
  })
  const remove = useMutation({
    mutationFn: () => deleteDeal(recordId),
    onSuccess: () => {
      setConfirmDelete(false)
      void queryClient.invalidateQueries({ queryKey: ['list', 'sales.deal'] })
      void queryClient.invalidateQueries({ queryKey: ['deal-board'] })
      navigate(listPath())
    },
    onError: (error) => {
      setConfirmDelete(false)
      fail(error)
    },
  })
  const complete = useMutation({
    mutationFn: (activityId: string) => completeActivity(activityId),
    onSuccess: refreshActivity,
    onError: fail,
  })

  const current = deal.data
  const stages = useMemo(
    () =>
      [...(funnels.data?.find((funnel) => funnel.id === current?.funnelId)?.stages ?? [])].sort(
        (a, b) => a.stageOrder - b.stageOrder,
      ),
    [funnels.data, current?.funnelId],
  )
  const open = current?.status === 'open'
  const nextStage = open
    ? stages.find((stage) => stage.stageType === 'open' && stage.stageOrder > (current?.stageOrder ?? 0))
    : undefined
  const lostStage = stages.find((stage) => stage.stageType === 'lost')

  const selectStage = (stageId: string) => {
    const stage = stages.find((candidate) => candidate.id === stageId)
    if (!stage) return
    setFailure(null)
    if (stage.stageType === 'lost' || stage.stageType === 'won') {
      setClosing({ stageCode: stage.code, kind: stage.stageType })
    } else {
      move.mutate({ stageCode: stage.code, reasonId: null, note: null })
    }
  }

  // Alt+→ — the design's hotkey for the next stage. A key typed into a field is a
  // character, not a command.
  const nextStageId = mayUpdate && !closing && !move.isPending ? nextStage?.id : undefined
  useEffect(() => {
    if (!nextStageId) return
    const onKey = (event: KeyboardEvent) => {
      if (!event.altKey || event.key !== 'ArrowRight' || isTyping(event.target)) return
      event.preventDefault()
      selectStage(nextStageId)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  })

  if (deal.isLoading) {
    return (
      <div className="rec-page" aria-busy="true" aria-label={t('app.loading')}>
        <span className="skel" style={{ width: 340, height: 20 }} />
        <span className="skel" style={{ height: 44, borderRadius: 6 }} />
        <span className="skel" style={{ height: 64, borderRadius: 8 }} />
      </div>
    )
  }
  if (!current) {
    const forbidden = deal.error instanceof ApiError && deal.error.isForbidden
    return (
      <StateView
        boxed
        kind={forbidden ? 'forbidden' : 'error'}
        title={forbidden ? t('record.state.forbidden.title') : t('record.state.error.title')}
        description={forbidden ? t('record.state.forbidden.text') : t('record.state.error.text')}
        actions={
          <>
            {!forbidden && (
              <button type="button" className="btn btn-primary btn-sm" onClick={() => void deal.refetch()}>
                <Icon name="refresh" size="sm" />
                {t('record.state.retry')}
              </button>
            )}
            <Link to={listPath()} className="btn btn-ghost btn-sm">
              {t('record.state.toList')}
            </Link>
          </>
        }
      />
    )
  }

  // ------------------------------------------------------------------ шапка

  const counterpartyHref = current.counterpartyId
    ? cardHref(user, 'sales.counterparty', current.counterpartyId)
    : null
  const meta = [
    current.counterpartyName ? (
      <span key="client" className="row" style={{ gap: 6 }}>
        <Icon name="building" size="sm" />
        {counterpartyHref ? <Link to={counterpartyHref}>{current.counterpartyName}</Link> : current.counterpartyName}
      </span>
    ) : (
      <span key="client" className="muted">
        {t('record.deal.noClient')}
      </span>
    ),
    !open ? (
      <CellValue
        key="status"
        values={{ status: current.status }}
        column="status"
        descriptor={{ name: 'status', type: 'text', sortable: false, filterable: false }}
        entity="sales.deal"
      />
    ) : null,
    <CopyNumber key="number" number={current.number} />,
  ]

  const primary: HeaderAction | null =
    mayUpdate && nextStage && !closing
      ? {
          id: 'next-stage',
          label: t('record.deal.nextStage', { stage: nextStage.name }),
          onClick: () => selectStage(nextStage.id),
          disabled: move.isPending,
          kbd: 'Alt →',
        }
      : null

  const menu: MenuAction[] = [
    {
      id: 'copy-link',
      label: t('record.copyLink'),
      icon: 'link',
      onSelect: () => {
        void navigator.clipboard?.writeText(window.location.href).catch(() => setFailure(t('record.copyFailed')))
      },
    },
  ]
  if (mayUpdate && open && lostStage) {
    menu.push({ id: 'close-lost', label: t('record.deal.closeLost'), icon: 'close', onSelect: () => selectStage(lostStage.id) })
  }
  // Only an open deal is deleted: a closed one is already an invoice or a line of the
  // loss-reasons report.
  if (mayDelete && open) {
    menu.push({ id: 'delete', label: t('record.deal.delete'), icon: 'trash', danger: true, onSelect: () => setConfirmDelete(true) })
  }

  // ------------------------------------------------------------------ полоса стадий

  const openStages = stages.filter((stage) => stage.stageType === 'open')
  const visited = new Set((history.data ?? []).map((entry) => entry.toStageId))
  const steps: StageStep[] = openStages.map((stage) => ({
    id: stage.id,
    label: stage.name,
    state: stepState(stage, current, visited),
    warning: stage.id === current.stageId && current.stuck,
    meta: stepMeta(stage, current, history.data ?? [], t),
  }))
  const ends = stages
    .filter((stage) => stage.stageType === 'won' || stage.stageType === 'lost')
    .map((stage) => ({
      id: stage.id,
      label: stage.name,
      tone: stage.stageType as 'won' | 'lost',
      active: stage.id === current.stageId,
    }))

  // ------------------------------------------------------------------ факты

  const facts: Fact[] = [
    {
      id: 'amount',
      label: t('record.deal.fact.amount'),
      value: formatMoney(current.amount, current.currency, locale),
      sub: t('record.deal.fact.positions', { count: current.lines.length }),
    },
    {
      id: 'probability',
      label: t('record.deal.fact.probability'),
      value: `${current.probability} %`,
      sub: t('record.deal.fact.weighted', { amount: formatMoney(current.weightedAmount, current.currency, locale) }),
    },
    // The model's forecast next to the stage's static probability, never in its place (#128):
    // the static one is what the weighted pipeline counts, the forecast is what to check it
    // against. «Why» is the top factors in words; no forecast is said in words too.
    {
      id: 'forecast',
      label: t('record.deal.fact.forecast'),
      value: current.forecast ? (
        <span data-testid="deal-forecast">{formatPercent(current.forecast.probability)}</span>
      ) : (
        <span className="t-sm muted" data-testid="deal-forecast-none">
          {noForecastText(t, current.forecastStatus)}
        </span>
      ),
      sub: current.forecast ? <ForecastFactors forecast={current.forecast} /> : undefined,
    },
    {
      id: 'close',
      label: t('record.deal.fact.closeOn'),
      value: current.expectedCloseOn ? formatDate(current.expectedCloseOn, locale) : t('record.notSet'),
      sub: closeSub(current, locale, t),
    },
    {
      id: 'responsible',
      label: t('record.deal.fact.responsible'),
      value: (
        <span className="t-md" style={{ fontWeight: 500 }}>
          {labelOf(record.values, 'responsible_employee_id') ?? t('record.notSet')}
        </span>
      ),
      sub: labelOf(record.values, 'department_id') ?? undefined,
    },
  ]

  // ------------------------------------------------------------------ лента и шаг

  const mayComment = can(user, 'system.comment', 'create')
  const modes: ComposerMode[] = []
  if (mayComment) {
    modes.push({
      id: 'comment',
      label: t('timeline.kind.comment'),
      icon: 'comment',
      placeholder: t('record.composer.comment'),
      mentions: searchMentionable,
      submit: async ({ text, mentionedUserIds }) => {
        await postComment('sales.deal', recordId, text, { mentionedUserIds })
        refreshActivity()
      },
    })
  }
  // A call is work done by a person in the org chart: an account without an employee
  // card has nobody to attribute it to, and the server would refuse.
  const mayLog = mayUpdate && mayLogActivity(user)
  if (mayLog && user?.employeeId) {
    const employeeId = user.employeeId
    modes.push({
      id: 'call',
      label: t('timeline.kind.call'),
      icon: 'phone',
      placeholder: t('record.composer.call'),
      extra: 'duration',
      submit: async ({ text, durationMinutes }) => {
        await logCall({
          entityType: 'deal',
          entityId: recordId,
          subject: text,
          direction: 'outbound',
          durationSeconds: durationMinutes === undefined ? null : Math.round(durationMinutes * 60),
          employeeId,
        })
        refreshActivity()
      },
    })
  }
  if (mayLog) {
    modes.push({
      id: 'task',
      label: t('timeline.kind.task'),
      icon: 'work',
      placeholder: t('record.composer.task'),
      extra: 'due',
      submit: async ({ text, due }) => {
        await createTask({
          entityType: 'deal',
          entityId: recordId,
          subject: text,
          assigneeId: user?.employeeId ?? current.responsibleEmployeeId,
          dueAt: due ? new Date(due).toISOString() : null,
        })
        refreshActivity()
      },
    })
  }
  const taskModeAvailable = modes.some((mode) => mode.id === 'task')

  const step = activities.data ? nextStepOf(activities.data) : null
  const feedItems = (timeline.data?.events ?? []).map((event) => feedItemOf(event, t))

  const overview = (
    <div className="layout-2-wide">
      <div className="rec-col">
        {/* A closed deal has no next step to plan; asking for one would be noise. */}
        {open && activities.isSuccess && (
          <NextStep
            task={step ? nextStepTask(step, locale, t) : null}
            actions={
              step && mayUpdate && mayCompleteActivity(user, step) ? (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={complete.isPending}
                  onClick={() => complete.mutate(step.id)}
                >
                  <Icon name="check" size="sm" />
                  {t('record.next.done')}
                </button>
              ) : undefined
            }
            missingHint={t('record.deal.next.missingHint')}
            missingAction={
              taskModeAvailable ? (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setComposerMode('task')
                    setComposerFocus((value) => value + 1)
                  }}
                >
                  {t('record.next.assign')}
                </button>
              ) : undefined
            }
          />
        )}
        {activities.isError && (
          <StateView compact kind="error" title={t('record.deal.next.error')} />
        )}

        {modes.length > 0 && (
          <Composer modes={modes} mode={composerMode} onModeChange={setComposerMode} focusKey={composerFocus} />
        )}
        {open && (
          <PlanActivityButtons target={{ type: 'deal', id: recordId, label: current.number }} />
        )}

        <NextStepPanel dealId={recordId} />
        <TaskProposalsPanel entityType="deal" entityId={recordId} />
        <ConversationSummaryPanel entityType="deal" entityId={recordId} />

        {timeline.isLoading ? (
          <p className="muted">{t('app.loading')}</p>
        ) : timeline.isError ? (
          <StateView
            compact
            kind={timeline.error instanceof ApiError && timeline.error.isForbidden ? 'forbidden' : 'error'}
            title={t('record.deal.feed.error')}
          />
        ) : (
          <Feed
            items={feedItems}
            empty={
              <StateView compact kind="empty" title={t('record.deal.feed.empty.title')} description={t('record.deal.feed.empty.text')} />
            }
            footer={<TimelineMore feed={timeline} />}
          />
        )}
      </div>

      <div className="rec-col">
        <Panel
          title={t('record.deal.details')}
          actions={
            edit.editing ? (
              <>
                <button type="button" className="btn btn-ghost btn-sm" onClick={edit.cancel} disabled={edit.saving}>
                  {t('card.cancel')}
                </button>
                <button type="button" className="btn btn-primary btn-sm" onClick={edit.submit} disabled={edit.saving}>
                  {edit.saving ? t('card.saving') : t('card.save')}
                </button>
              </>
            ) : edit.mayUpdate ? (
              <button type="button" className="btn btn-ghost btn-sm" onClick={edit.begin}>
                {t('card.edit')}
              </button>
            ) : undefined
          }
        >
          {form}
        </Panel>

        <Panel
          title={t('record.deal.positions')}
          count={current.lines.length}
          flush
          actions={
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTab('lines')}>
              {t('record.deal.allPositions')}
            </button>
          }
        >
          <PositionsSummary deal={current} />
        </Panel>

        <Panel title={t('participant.title')}>{participantsTab().render(context)}</Panel>

        <Panel title={t('card.tab.customFields')}>
          <CustomFieldsPanel entity={context.entity} recordId={recordId} resource={context.resource} />
        </Panel>
      </div>
    </div>
  )

  const documents = (
    <div className="rec-col" style={{ paddingTop: 14 }}>
      {mayReadQuotes && (
        <Panel title={t('record.deal.quotes')} count={quotes.data?.length ?? null} flush>
          {quotes.isLoading ? (
            <div className="panel-b muted">{t('app.loading')}</div>
          ) : quotes.isError ? (
            <StateView compact kind="error" title={t('record.deal.quotes.error')} />
          ) : (quotes.data ?? []).length === 0 ? (
            <div className="panel-b secondary">{t('record.deal.quotes.empty')}</div>
          ) : (
            (quotes.data ?? []).map((quote) => (
              <div key={quote.id} className="list-item">
                <Icon name="file" className="muted" />
                <div className="stack" style={{ flex: 1, gap: 0 }}>
                  <span className="mono">{quote.number}</span>
                  <span className="t-xs muted">
                    {[
                      quote.issuedOn ? formatDate(quote.issuedOn, locale) : null,
                      formatMoney(quote.total, quote.currency, locale),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
                <CellValue
                  values={{ status: quote.status }}
                  column="status"
                  descriptor={{ name: 'status', type: 'text', sortable: false, filterable: false }}
                  entity="sales.quote"
                />
              </div>
            ))
          )}
        </Panel>
      )}
      <Panel title={t('card.tab.attachments')}>
        <AttachmentsPanel entityType={context.entity} entityId={recordId} resource={context.resource} />
      </Panel>
      <Panel title={t('card.tab.related')}>
        <p>{t('card.related.missing')}</p>
        <p className="muted">{t('card.related.reason')}</p>
      </Panel>
    </div>
  )

  const historyTab = (
    <div className="rec-col" style={{ paddingTop: 14 }}>
      <Panel title={t('card.tab.stageHistory')}>
        <StageHistoryPanel recordId={recordId} />
      </Panel>
      <Panel title={t('record.history.changes')}>
        <HistoryPanel entity={context.entity} recordId={recordId} />
      </Panel>
    </div>
  )

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[{ label: t('nav.deals'), to: listPath() }, { label: current.number, mono: true }]}
        title={current.title}
        meta={meta}
        primary={primary}
        secondary={[dial.action, mail.action].filter((action): action is HeaderAction => action != null)}
        menu={menu}
        menuLabel={t('record.more')}
      />
      {mail.dialog}
      {dial.outcome}

      {failure && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span>{failure}</span>
        </div>
      )}
      {notices}

      <StageBar
        steps={steps}
        ends={ends}
        ariaLabel={t('record.deal.stages')}
        busy={move.isPending}
        onSelect={mayUpdate ? selectStage : undefined}
      />
      {closing && (
        <CloseDealForm
          kind={closing.kind}
          title={current.title}
          pending={move.isPending}
          onCancel={() => setClosing(null)}
          onConfirm={(reasonId, note) => move.mutate({ stageCode: closing.stageCode, reasonId, note })}
        />
      )}

      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          { id: 'overview', label: t('record.tab.overview') },
          { id: 'lines', label: t('record.tab.positions'), count: current.lines.length },
          { id: 'documents', label: t('record.tab.documents'), count: mayReadQuotes ? quotes.data?.length ?? null : null },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('record.deal.sections')}
      >
        {tab === 'overview' && <div style={{ paddingTop: 14 }}>{overview}</div>}
        {tab === 'lines' && <LinesPanel recordId={recordId} />}
        {tab === 'documents' && documents}
        {tab === 'history' && historyTab}
      </RecordTabs>

      <ConfirmModal
        open={confirmDelete}
        title={t('record.deal.delete.title', { title: current.title })}
        confirmLabel={t('record.deal.delete')}
        pending={remove.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate()}
      >
        <p>{t('record.deal.delete.text')}</p>
        <p className="strong" style={{ color: 'var(--fg)' }}>
          {t('record.irreversible')}
        </p>
      </ConfirmModal>
    </div>
  )
}

/** Позиции кратко: название, количество, сумма; полная таблица — на вкладке. */
function PositionsSummary({ deal }: { deal: DealCard }) {
  const { t, locale } = useApp()
  if (deal.lines.length === 0) {
    return <div className="panel-b secondary">{t('record.deal.positions.empty')}</div>
  }
  return (
    <table className="tbl">
      <thead>
        <tr>
          <th>{t('card.deal.line.product')}</th>
          <th className="num">{t('card.deal.line.quantity')}</th>
          <th className="num">{t('card.deal.line.total')}</th>
        </tr>
      </thead>
      <tbody>
        {deal.lines.map((line) => (
          <tr key={line.id}>
            <td className="wrap">
              <span className="stack" style={{ gap: 0 }}>
                <span>{line.productName}</span>
                <span className="mono muted t-xs">{line.productSku}</span>
              </span>
            </td>
            <td className="num">{line.quantity}</td>
            <td className="num">{formatMoney(line.lineTotal, deal.currency, locale)}</td>
          </tr>
        ))}
        <tr className="total">
          <td>{t('card.deal.lines.total')}</td>
          <td />
          <td className="num">{formatMoney(deal.amount, deal.currency, locale)}</td>
        </tr>
      </tbody>
    </table>
  )
}

/** Кнопка копирования номера: номер диктуют по телефону и вставляют в письма. */
function CopyNumber({ number }: { number: string }) {
  const { t } = useApp()
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      className="copyable mono t-sm"
      style={{ border: 0, background: 'transparent', cursor: 'pointer' }}
      aria-label={t('record.copyNumber', { number })}
      onClick={() => {
        void navigator.clipboard?.writeText(number).then(() => setCopied(true), () => undefined)
      }}
    >
      {number}
      <Icon name={copied ? 'check' : 'copy'} size="xs" />
    </button>
  )
}

type Translate = (key: string, params?: Record<string, string | number>) => string

function stepState(stage: FunnelStage, deal: DealCard, visited: Set<string>): StageStep['state'] {
  if (stage.id === deal.stageId) return 'current'
  // An open deal is "past" every earlier stage by position; a closed one has no position
  // on the open stages, so only the stages it actually went through count as passed.
  if (deal.status === 'open') return stage.stageOrder < deal.stageOrder ? 'done' : 'future'
  return visited.has(stage.id) ? 'done' : 'future'
}

function stepMeta(stage: FunnelStage, deal: DealCard, history: StageHistoryEntry[], t: Translate): string | undefined {
  if (stage.id === deal.stageId) {
    const days = t('card.deal.history.days', { days: deal.daysInStage })
    if (deal.maxDaysInStage === null) return days
    return deal.stuck
      ? t('record.stage.overLimit', { days: deal.daysInStage, limit: deal.maxDaysInStage })
      : t('record.stage.withinLimit', { days: deal.daysInStage, limit: deal.maxDaysInStage })
  }
  const spent = history
    .filter((entry) => entry.fromStageId === stage.id && entry.durationSeconds !== null)
    .reduce((sum, entry) => sum + (entry.durationSeconds ?? 0), 0)
  if (spent > 0) return formatDuration(spent, t)
  return deal.status === 'open' && stage.stageOrder > deal.stageOrder ? `${stage.probability} %` : undefined
}

/** Срок закрытия словами: «через 21 дн.» или флаг «просрочено» — иконка и слово, не цвет. */
function closeSub(deal: DealCard, locale: string, t: Translate) {
  if (deal.status !== 'open') {
    const closed = deal.closedAt ? new Date(deal.closedAt) : null
    // A moment, not a calendar date: the day is the user's, not UTC's.
    return closed && !Number.isNaN(closed.getTime())
      ? t('record.deal.closedOn', { date: closed.toLocaleDateString(locale === 'en' ? 'en-GB' : 'ru-RU') })
      : undefined
  }
  if (!deal.expectedCloseOn) return undefined
  const days = daysUntil(deal.expectedCloseOn)
  if (days === null) return undefined
  if (days < 0) {
    return (
      <span className="flag flag-danger">
        <Icon name="alert" />
        {t('record.deal.overdueBy', { days: -days })}
      </span>
    )
  }
  return days === 0 ? t('record.deal.dueToday') : t('record.deal.dueIn', { days })
}

/** Дней до календарной даты от сегодняшней местной полуночи. */
export function daysUntil(isoDate: string, now: Date = new Date()): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate)
  if (!match) return null
  const target = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).getTime()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  return Math.round((target - today) / 86_400_000)
}

function labelOf(values: Record<string, unknown>, column: string): string | null {
  const label = values[`${column}__label`]
  return typeof label === 'string' && label !== '' ? label : null
}

function nextStepTask(step: Activity, locale: string, t: Translate) {
  const due = step.dueAt ? new Date(step.dueAt) : null
  const when =
    due && !Number.isNaN(due.getTime())
      ? due.toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : t('record.next.noDue')
  return { title: step.subject, details: when, overdue: step.overdue, icon: 'work' as const }
}

function feedItemOf(event: TimelineEvent, t: Translate): FeedItem {
  if (event.kind === 'stage') {
    return {
      id: event.id,
      kind: 'stage',
      at: event.at,
      title: (
        <>
          {t('record.feed.stage', {
            from: String(event.details.fromStage || t('timeline.stage.created')),
            to: String(event.details.toStage ?? ''),
          })}
          {/* A step back is marked with a word: colour survives neither print nor
              colour blindness, and going back is what the feed is read for. */}
          {event.details.backwards === true && (
            <span className="badge b-warning" style={{ marginLeft: 6 }}>
              {t('timeline.stage.backwards')}
            </span>
          )}
        </>
      ),
    }
  }
  // Every other kind reads the same on every card (#114): one rendering, not a copy per card.
  return sharedFeedItemOf(event, t)
}

function listPath(): string {
  const item = NAVIGATION.flatMap((section) => section.items).find((candidate) => candidate.listEntity === 'sales.deal')
  return item?.path ?? '/deals'
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable === true
}
